import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useHistory } from "react-router-dom";
import { Button, Typography } from "@mui/material";
import ApiService from "../../services/api-service";
import Loader from "../Loader";
import { useDispatch, useSelector } from "react-redux";
import { updatecount } from "../../action/adv";
import { start } from "../../action/streamSong";
import AudPlayer from "../AudPlayer";
import ImageAdv from "../ImageAdv";
import "./index.scss";

const DEFAULT_SKIP_AFTER_SECONDS = 5;
const DEFAULT_IMAGE_AD_SECONDS = 30;
const PREMIUM_PATH = "/app/Premium";

/**
 * Full-screen ad between songs (and on the livestream gate) for free-tier listeners.
 *
 * - Fetches one direct-sold ad from `/ad/next`; nothing to show ends the break at once.
 * - Reports one impression per ad and links a click on the creative to it.
 * - One skip countdown for every ad type, starting at the ad's `skipAfterSeconds`.
 * - "Go ad-free" ends the break and takes the listener to Premium.
 * Ending the break means `updatecount(0)`: AppContainer unmounts this and the Player resumes.
 */
export default function Advertisement() {
  const history = useHistory();
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { index, songs, track, prevIndex, pos } = useSelector(
    (state) => state.streamSong
  );
  const { count } = useSelector((state) => state.adv);
  const dispatch = useDispatch();
  const [add, setAdd] = useState();
  const [impressionId, setImpressionId] = useState(null);
  const [skipIn, setSkipIn] = useState(null);
  const { isStream } = useSelector((state) => state.stream);

  const endAd = () => dispatch(updatecount(0));

  const getAdd = async () => {
    try {
      setLoading(true);
      const response = await ApiService.AD_NEXT();
      const ad = response?.result;
      if (!ad || !ad.id) {
        // No direct ad available — skip the break entirely
        // (AdSense cannot be shown on interstitial/full-screen ad-only screens per policy)
        endAd();
        setLoading(false);
        return;
      }

      const newImpressionId = uuidv4();
      setImpressionId(newImpressionId);
      setAdd(ad);
      setSkipIn(
        Number.isFinite(ad.skipAfterSeconds) && ad.skipAfterSeconds >= 0
          ? ad.skipAfterSeconds
          : DEFAULT_SKIP_AFTER_SECONDS
      );
      ApiService.AD_IMPRESSION({
        impressionId: newImpressionId,
        platform: "web",
        format: "direct",
        adId: ad.id,
        shownAt: new Date().toISOString(),
      }).catch((error) =>
        console.warn("[Advertisement] impression not recorded", error)
      );

      if (count && track) {
        track.pauseProcessAudioBuffer();
        dispatch(
          start({
            index: index,
            songs: songs,
            prevIndex: prevIndex,
            pos: pos,
            track: track,
          })
        );
      }
      if (isStream) {
        const adContainer = document.getElementById("_ad_container");
        if (adContainer) {
          adContainer.style.height = "calc(100vh - 60px)";
        }
      }

      setLoading(false);
    } catch (error) {
      // Backend error — skip the break entirely
      console.warn("[Advertisement] could not load an ad", error);
      endAd();
      setLoading(false);
    }
  };
  useEffect(() => {
    getAdd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipIn > 0) {
      const timerId = setTimeout(() => {
        setSkipIn(skipIn - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    }
  }, [skipIn]);

  function handleSeeking(event) {
    const video = videoRef.current;
    const currentTime = video?.currentTime;
    const previousTime = event.target.dataset.previousTime;

    if (currentTime > previousTime) {
      video.currentTime = previousTime;
    }
    event.target.dataset.previousTime = video?.currentTime;
  }

  useEffect(() => {
    function vidCtrl(e) {
      const vid = document.querySelector("video");
      if (e.code === "Space") {
        e.preventDefault();
        if (vid?.play || vid?.ended) {
          vid.pause(false);
        }
      }
    }
    window.addEventListener("keydown", vidCtrl);
    return () => window.removeEventListener("keydown", vidCtrl);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => dispatch(updatecount(0));
    video.addEventListener("ended", handleEnded);
    return () => video.removeEventListener("ended", handleEnded);
  }, [add, dispatch]);

  const canSkip = skipIn === 0;

  const handleSkip = (e) => {
    e.stopPropagation();
    if (!canSkip) return;
    endAd();
  };

  const handleGoAdFree = (e) => {
    e.stopPropagation();
    endAd();
    history.push(PREMIUM_PATH);
  };

  const handleCreativeClick = (e) => {
    if (!add) return;
    ApiService.AD_CLICK({ adId: add.id, impressionId }).catch((error) =>
      console.warn("[Advertisement] click not recorded", error)
    );
    // Image and audio creatives are wrapped in their own <a target="_blank">,
    // which the browser already follows; opening it again would spawn two tabs.
    const viaAnchor = e.target?.closest?.("a");
    if (viaAnchor || !add.redirectUrl) return;
    let a = document.createElement("a");
    a.target = "_blank";
    a.rel = "noopener";
    a.href = add.redirectUrl;
    a.click();
    a.remove();
  };

  if (!add) {
    return loading ? (
      <div className="_ad_container" id="_ad_container">
        <Loader />
      </div>
    ) : null;
  }

  return (
    <div
      className="_ad_container"
      id="_ad_container"
      data-testid="advertisement"
      onClick={handleCreativeClick}
    >
      <div className="_ad_controls">
        <Button
          variant="contained"
          disableElevation
          className="_ad_go_ad_free"
          onClick={handleGoAdFree}
          data-testid="ad-go-ad-free"
        >
          Go ad-free
        </Button>
        {/* Not `disabled`: React drops onClick on disabled buttons, so an early tap
            would bubble to the creative and count as an ad click. */}
        <button
          type="button"
          className={"_ad_skip" + (canSkip ? " _ad_skip--ready" : "")}
          onClick={handleSkip}
          aria-disabled={!canSkip}
          data-testid="ad-skip"
        >
          <Typography component="span" className="fs-20">
            {canSkip ? "Skip" : `Skip in ${skipIn}s`}
          </Typography>
        </button>
      </div>

      {add.url && add.type === "video" ? (
        <>
          <video
            className="video_container"
            controls
            data-setup="{}"
            src={add.url}
            ref={videoRef}
            controlsList="nodownload nofullscreen noremoteplayback noplaybackrate nopacture "
            disablePictureInPicture
            onSeeking={handleSeeking}
            autoPlay
            currentTime="false"
          ></video>
          <div className="transferent"></div>
        </>
      ) : add.url && add.type === "image" ? (
        <ImageAdv
          url={add.url}
          type={add.type}
          href={add.redirectUrl ? add.redirectUrl : ""}
          time={add.duration > 0 ? add.duration : DEFAULT_IMAGE_AD_SECONDS}
        />
      ) : add.url && add.type === "audio" ? (
        <AudPlayer
          url={add.url}
          image={add.thumbnailImg}
          type={add.type}
          href={add.redirectUrl ? add.redirectUrl : ""}
        />
      ) : null}

      {loading ? <Loader /> : null}
    </div>
  );
}
